import { useState } from "react";

import { Check, Close, Edit } from "@mui/icons-material";
import {
  Box,
  Chip,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import usePost from "../../app/usePost";
import { axiosApi } from "../../interceptors/axios";
import { Result } from "../../types/Result";
import { Question, QuestionOption } from "../exam/type";

interface AnswerItemProps {
  option: QuestionOption;
  showAnswers?: boolean;
  question: Question;
}

export default function AnswerItem(props: AnswerItemProps) {
  const { option, showAnswers, question } = props;
  const [isSelected, setSelectedOption] = useState(false);
  const { post, waiting } = usePost<QuestionOption>("Soru güncellendi");
  const [editedOption, setEditedOption] = useState(false);
  const [editedContent, setEditedContent] = useState<string>(option.content);
  const isCorrect =
    showAnswers && isSelected && option.option === question.answer;
  const isWrong = showAnswers && option.option !== question.answer;

  const handleEditClick = () => {
    setEditedOption(!editedOption);
  };

  const handleOptionSelect = () => {
    setSelectedOption(!isSelected);
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedContent(e.target.value);
  };

  const handleSaveClick = () => {
    post(
      axiosApi
        .put<Result<QuestionOption>>(`api/questionOptions`, {
          ...option,
          content: editedContent,
        })
        .then((r) => {
          setEditedOption(false);
          setEditedContent(r.data.data!.content);
          return r;
        })
        .catch((e) => {
          setEditedContent(option.content);
          throw e;
        })
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        key={option.option}
        onClick={() => handleOptionSelect()}
        sx={{
          display: "flex",
          mb: 1,
          p: 1,
          borderRadius: 1,
          cursor: "pointer",
          bgcolor: isCorrect
            ? "rgba(46, 125, 50, 0.1)"
            : isWrong
            ? "rgba(211, 47, 47, 0.1)"
            : "transparent",
          "&:hover": {
            bgcolor: isCorrect
              ? "rgba(46, 125, 50, 0.2)"
              : isWrong
              ? "rgba(211, 47, 47, 0.2)"
              : "rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: 28,
            height: 28,
            borderRadius: "50%",
            mr: 2,
            border: "1px solid",
            borderColor: isSelected ? "primary.main" : "grey.400",
            bgcolor: isSelected ? "primary.main" : "transparent",
            color: isSelected ? "white" : "inherit",
            fontWeight: "bold",
          }}
        >
          {option.option}
        </Box>

        <Grid container size={{ xs: 12 }} spacing={1}>
          <Grid size={{ xs: "grow" }}>
            {!editedOption ? (
              <Typography
                component="span"
                dangerouslySetInnerHTML={{ __html: editedContent }}
              />
            ) : (
              <TextField
                value={editedContent}
                multiline
                rows={4}
                fullWidth
                onChange={handleContentChange}
              />
            )}
            {isCorrect && (
              <Chip
                label="Doğru Cevap"
                color="success"
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Grid>
          <Grid size={{ xs: "auto" }} alignItems={"flex-end"}>
            {!editedOption ? (
              <IconButton color="primary" onClick={() => handleEditClick()}>
                <Edit fontSize="small" />
              </IconButton>
            ) : (
              <>
                <IconButton
                  color="success"
                  onClick={() => handleEditClick()}
                  disabled={waiting}
                >
                  <Close fontSize="small" />
                </IconButton>
                <IconButton
                  color="success"
                  onClick={() => handleSaveClick()}
                  disabled={waiting}
                >
                  <Check fontSize="small" />
                </IconButton>
              </>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
